/**
 * チバリヨ - ホーム画面 (Phase 2-B-5)
 *
 * 構成:
 *   - 挨拶 (日付・曜日・時間帯別メッセージ)
 *   - 今日の名言 (public/data/quotes.json から日替わり。日付シードで全員同じ名言)
 *   - 全国8地点の今日の天気 (Open-Meteo。APIキー不要・ブラウザから直接取得)
 *   - 初回表示時のみ 5秒カウントダウンで自動遷移 (admin -> P1 / sales -> P7)
 *
 * 設計メモ:
 *   - Firestore には一切アクセスしない (ルール変更不要)
 *   - 天気取得失敗時はエラーメッセージ表示のみ (画面は壊れない)
 *   - 自動遷移タイマーは module スコープで管理し、再描画時に必ずクリア。
 *     tick ごとにコンテナの表示状態を確認し、他画面へ移動済みなら静かに停止する
 *     (ナビ移動後に勝手に P1/P7 へ引き戻される事故の防止)
 */

const WEATHER_LOCATIONS = [
  { name: '福岡市博多区', lat: 33.5904, lon: 130.4203 },
  { name: '大阪市',       lat: 34.6937, lon: 135.5023 },
  { name: '鹿児島市',     lat: 31.5966, lon: 130.5571 },
  { name: '愛知県田原市', lat: 34.6690, lon: 137.2645 },
  { name: '群馬県嬬恋村', lat: 36.5176, lon: 138.5297 },
  { name: '青森市',       lat: 40.8246, lon: 140.7406 },
  { name: '茨城県水戸市', lat: 36.3659, lon: 140.4712 },
  { name: '札幌市',       lat: 43.0618, lon: 141.3545 }
];

const REDIRECT_SECONDS = 5;

const REDIRECT_LABEL = {
  p1: 'ダッシュボード',
  p7: '営業ダッシュボード'
};

/** WMO weather code -> 絵文字 + ラベル */
function weatherCodeInfo(code) {
  if (code === 0)                      return { icon: '☀️', label: '快晴' };
  if (code === 1)                      return { icon: '🌤️', label: '晴れ' };
  if (code === 2)                      return { icon: '⛅', label: '晴れ時々曇り' };
  if (code === 3)                      return { icon: '☁️', label: '曇り' };
  if (code === 45 || code === 48)      return { icon: '🌫️', label: '霧' };
  if (code >= 51 && code <= 57)        return { icon: '🌦️', label: '霧雨' };
  if (code >= 61 && code <= 67)        return { icon: '🌧️', label: '雨' };
  if (code >= 71 && code <= 77)        return { icon: '❄️', label: '雪' };
  if (code >= 80 && code <= 82)        return { icon: '🌦️', label: 'にわか雨' };
  if (code === 85 || code === 86)      return { icon: '🌨️', label: 'にわか雪' };
  if (code >= 95)                      return { icon: '⛈️', label: '雷雨' };
  return { icon: '🌡️', label: '—' };
}

function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/* ---- 自動遷移タイマー (module スコープで一元管理) ---- */
let redirectIntervalId = null;

function cancelRedirect(container) {
  if (redirectIntervalId !== null) {
    clearInterval(redirectIntervalId);
    redirectIntervalId = null;
  }
  const bar = container ? container.querySelector('.home-redirect-bar') : null;
  if (bar) bar.style.display = 'none';
}

/* ---- 名言 (日替わり: 日付シードで全員同じ) ---- */
let quotesCache = null;

async function loadQuotes() {
  if (quotesCache) return quotesCache;
  const res = await fetch('/data/quotes.json', { cache: 'no-cache' });
  if (!res.ok) throw new Error('quotes.json の取得に失敗 (' + res.status + ')');
  quotesCache = await res.json();
  return quotesCache;
}

function pickDailyQuote(quotes) {
  const d = new Date();
  const key = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
  // 単純な剰余だと月替わりで並びが近くなるため軽くハッシュを掛ける
  const idx = (key * 2654435761 % 4294967296) % quotes.length;
  return quotes[idx];
}

/* ---- 天気 (Open-Meteo: 8地点を1リクエストで取得) ---- */
async function fetchWeather() {
  const lats = WEATHER_LOCATIONS.map((l) => l.lat).join(',');
  const lons = WEATHER_LOCATIONS.map((l) => l.lon).join(',');
  const url =
    'https://api.open-meteo.com/v1/forecast' +
    '?latitude=' + lats +
    '&longitude=' + lons +
    '&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max' +
    '&timezone=Asia%2FTokyo&forecast_days=1';

  const res = await fetch(url);
  if (!res.ok) throw new Error('天気APIエラー (' + res.status + ')');
  const data = await res.json();
  // 地点が複数の場合は配列、1件の場合はオブジェクトで返る
  return Array.isArray(data) ? data : [data];
}

function renderWeatherTiles(gridEl, results) {
  gridEl.innerHTML = WEATHER_LOCATIONS.map((loc, i) => {
    const d = results[i] && results[i].daily;
    if (!d) {
      return (
        '<div class="home-weather-tile">' +
          '<div class="home-weather-place">' + esc(loc.name) + '</div>' +
          '<div class="home-weather-error">取得失敗</div>' +
        '</div>'
      );
    }
    const info = weatherCodeInfo(d.weather_code[0]);
    const tmax = Math.round(d.temperature_2m_max[0]);
    const tmin = Math.round(d.temperature_2m_min[0]);
    const pop = d.precipitation_probability_max ? d.precipitation_probability_max[0] : null;
    return (
      '<div class="home-weather-tile">' +
        '<div class="home-weather-place">' + esc(loc.name) + '</div>' +
        '<div class="home-weather-icon">' + info.icon + '</div>' +
        '<div class="home-weather-label">' + esc(info.label) + '</div>' +
        '<div class="home-weather-temp">' +
          '<span class="home-temp-max">' + tmax + '°</span>' +
          '<span class="home-temp-sep">/</span>' +
          '<span class="home-temp-min">' + tmin + '°</span>' +
        '</div>' +
        (pop != null
          ? '<div class="home-weather-pop">☔ ' + pop + '%</div>'
          : '<div class="home-weather-pop">&nbsp;</div>') +
      '</div>'
    );
  }).join('');
}

/* ---- 挨拶 ---- */
function buildGreeting(member) {
  const h = new Date().getHours();
  let msg;
  if (h < 4)       msg = 'お疲れさまです';
  else if (h < 11) msg = 'おはようございます';
  else if (h < 18) msg = 'こんにちは';
  else             msg = 'お疲れさまです';
  const name = member && member.name ? member.name : '';
  return name ? msg + '、' + name + ' さん' : msg;
}

function formatToday() {
  const d = new Date();
  const youbi = ['日', '月', '火', '水', '木', '金', '土'][d.getDay()];
  return d.getFullYear() + '年' + (d.getMonth() + 1) + '月' + d.getDate() + '日 (' + youbi + ')';
}

/* ========================================================================
 * エントリポイント
 * ====================================================================== */

/**
 * ホーム画面を描画する。
 * @param {HTMLElement} container - #home-content
 * @param {Object} sessionMember - セッションメンバー
 * @param {Object} opts
 *   - autoRedirect {boolean} 初回表示時 true。5秒後に自動遷移する
 *   - redirectTarget {'p1'|'p7'} 遷移先
 *   - onNavigate {Function} navigateTo 関数
 */
export function renderHomePage(container, sessionMember, opts = {}) {
  const autoRedirect = opts.autoRedirect === true && typeof opts.onNavigate === 'function';
  const redirectTarget = opts.redirectTarget || 'p1';
  const targetLabel = REDIRECT_LABEL[redirectTarget] || 'ダッシュボード';

  // 再描画時は前回タイマーを必ず破棄
  cancelRedirect(null);

  container.innerHTML =
    '<div class="home-page">' +

      (autoRedirect
        ? '<div class="home-redirect-bar">' +
            '<span class="home-redirect-text">' +
              '<span id="home-redirect-sec">' + REDIRECT_SECONDS + '</span> 秒後に' +
              esc(targetLabel) + 'へ移動します' +
            '</span>' +
            '<span class="home-redirect-actions">' +
              '<button id="home-go-now-btn" type="button" class="home-btn home-btn-primary">すぐ移動</button>' +
              '<button id="home-stay-btn" type="button" class="home-btn home-btn-ghost">このまま見る</button>' +
            '</span>' +
          '</div>'
        : '') +

      '<div class="home-card home-greeting-card">' +
        '<div class="home-date">' + esc(formatToday()) + '</div>' +
        '<h1 class="home-greeting">' + esc(buildGreeting(sessionMember)) + '</h1>' +
      '</div>' +

      '<div class="home-card home-quote-card">' +
        '<div class="home-card-title">今日の名言</div>' +
        '<div id="home-quote-body" class="home-quote-body">' +
          '<div class="home-loading">読み込み中…</div>' +
        '</div>' +
      '</div>' +

      '<div class="home-card home-weather-card">' +
        '<div class="home-card-title">今日の天気</div>' +
        '<div id="home-weather-grid" class="home-weather-grid">' +
          '<div class="home-loading">天気を取得しています…</div>' +
        '</div>' +
      '</div>' +
    '</div>';

  /* ---- 自動遷移 ---- */
  if (autoRedirect) {
    let remain = REDIRECT_SECONDS;
    const secEl = container.querySelector('#home-redirect-sec');

    redirectIntervalId = setInterval(() => {
      // 他画面へ移動済みなら静かに停止 (引き戻し事故防止)
      if (container.style.display === 'none' || !container.isConnected) {
        cancelRedirect(null);
        return;
      }
      remain -= 1;
      if (secEl) secEl.textContent = String(Math.max(remain, 0));
      if (remain <= 0) {
        cancelRedirect(container);
        opts.onNavigate(redirectTarget);
      }
    }, 1000);

    const goNowBtn = container.querySelector('#home-go-now-btn');
    if (goNowBtn) goNowBtn.onclick = () => {
      cancelRedirect(container);
      opts.onNavigate(redirectTarget);
    };

    const stayBtn = container.querySelector('#home-stay-btn');
    if (stayBtn) stayBtn.onclick = () => cancelRedirect(container);
  }

  /* ---- 名言 (非同期) ---- */
  const quoteBody = container.querySelector('#home-quote-body');
  loadQuotes()
    .then((quotes) => {
      if (!quotes || !quotes.length) throw new Error('名言データが空です');
      const q = pickDailyQuote(quotes);
      quoteBody.innerHTML =
        '<blockquote class="home-quote-text">' + esc(q.text) + '</blockquote>' +
        '<div class="home-quote-author">— ' + esc(q.author) + '</div>';
    })
    .catch((err) => {
      console.warn('[チバリヨ] 名言の取得に失敗:', err);
      quoteBody.innerHTML = '<div class="home-error">名言を読み込めませんでした</div>';
    });

  /* ---- 天気 (非同期) ---- */
  const weatherGrid = container.querySelector('#home-weather-grid');
  fetchWeather()
    .then((results) => {
      renderWeatherTiles(weatherGrid, results);
    })
    .catch((err) => {
      console.warn('[チバリヨ] 天気の取得に失敗:', err);
      weatherGrid.innerHTML =
        '<div class="home-error">天気情報を取得できませんでした (通信環境をご確認ください)</div>';
    });
}
